import { useMemo, useState, useCallback } from "react";

/**
 * useSortable: small client-side sorting helper for tables.
 *
 * Usage:
 *   const { items, sortKey, sortDir, requestSort, sortIcon } =
 *     useSortable(data, "name", "asc", { score: (row) => row.score ?? -1 });
 *
 *   <th onClick={() => requestSort("name")}>Nome {sortIcon("name")}</th>
 *
 * - `accessors` is optional and lets you customise how a value is extracted
 *   for comparison (useful when handling nulls or nested fields).
 * - Clicking the same column toggles asc → desc → none (returns original
 *   order for that column only; we keep current direction otherwise).
 */
export function useSortable(data, initialKey = null, initialDir = "asc", accessors = {}) {
    const [sortKey, setSortKey] = useState(initialKey);
    const [sortDir, setSortDir] = useState(initialDir);

    const requestSort = useCallback((key) => {
        if (sortKey === key) {
            // Cycle asc → desc → none
            if (sortDir === "asc") setSortDir("desc");
            else if (sortDir === "desc") { setSortKey(null); setSortDir("asc"); }
            else setSortDir("asc");
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    }, [sortKey, sortDir]);

    const items = useMemo(() => {
        if (!sortKey || !Array.isArray(data)) return data || [];
        const arr = [...data];
        const getter = accessors[sortKey] || ((row) => row?.[sortKey]);
        arr.sort((a, b) => {
            const va = getter(a);
            const vb = getter(b);
            // Push nulls/undefined to the end regardless of direction
            const aNil = va === null || va === undefined || va === "";
            const bNil = vb === null || vb === undefined || vb === "";
            if (aNil && bNil) return 0;
            if (aNil) return 1;
            if (bNil) return -1;
            if (typeof va === "number" && typeof vb === "number") {
                return sortDir === "asc" ? va - vb : vb - va;
            }
            const sa = String(va).toLocaleLowerCase("pt");
            const sb = String(vb).toLocaleLowerCase("pt");
            const cmp = sa.localeCompare(sb, "pt", { numeric: true, sensitivity: "base" });
            return sortDir === "asc" ? cmp : -cmp;
        });
        return arr;
    }, [data, sortKey, sortDir, accessors]);

    const sortIcon = useCallback((key) => {
        if (sortKey !== key) return "↕";
        return sortDir === "asc" ? "↑" : "↓";
    }, [sortKey, sortDir]);

    return { items, sortKey, sortDir, requestSort, sortIcon };
}
